import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import type { AppDispatch, RootState } from '../../store';
import {
  fetchMyTeams,
  fetchLeagueTeams,
  fetchPartnerRequests,
  acceptPartnerRequest,
  declinePartnerRequest,
  clearTeamError,
} from '../../store/slices/teamSlice';
import { Category, TeamStatus, TeamType } from '@sulis-liiga/common';
import type { Team } from '@sulis-liiga/common';
import { useActiveLeagueTenant } from '../../hooks/useActiveLeagueTenant';
import { getTeamLifecycle } from '../../utils/teamLifecycle';
import PartnerRequestsList from '../../components/team/PartnerRequestsList';
import ScopeToggle, { type Scope } from '../../components/common/ScopeToggle';

// ─── Constants ────────────────────────────────────────────────────────────────

const ALL_CATEGORIES: Category[] = [
  Category.MENS_SINGLES,
  Category.WOMENS_SINGLES,
  Category.MENS_DOUBLES,
  Category.WOMENS_DOUBLES,
  Category.MIXED_DOUBLES,
];

const TEAM_TYPE_LABELS: Record<TeamType, string> = {
  [TeamType.SINGLES]: 'Singles',
  [TeamType.DOUBLES]: 'Doubles',
  [TeamType.MIXED_DOUBLES]: 'Mixed Doubles',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  return name
    .split(' ')
    .map(n => n[0] ?? '')
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// ─── TeamCard ─────────────────────────────────────────────────────────────────

interface TeamCardProps {
  team: Team;
}

const TeamCard: React.FC<TeamCardProps> = ({ team }) => {
  const status = getTeamLifecycle(team);
  const hasEnrollments = (team.enrollments?.length ?? 0) > 0;
  const isAwaiting = team.status === TeamStatus.PENDING_PARTNER;

  return (
    <Link
      to={`/teams/${team.id}`}
      className="bg-white rounded-lg shadow-sm p-5 hover:shadow-md block transition-shadow"
      aria-label={`View team ${team.name}`}
    >
      {/* Header: name + status */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">{team.name}</h3>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${status.className}`}>
          {status.label}
        </span>
      </div>

      {/* Badges: type */}
      <div className="mt-2 flex flex-wrap gap-2 text-xs">
        <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-600">
          {TEAM_TYPE_LABELS[team.type]}
        </span>
        {hasEnrollments ? (
          <span className="px-2 py-0.5 rounded bg-primary-50 text-primary-700">
            {team.enrollments?.length ?? 0}{' '}
            {(team.enrollments?.length ?? 0) === 1 ? 'enrollment' : 'enrollments'}
          </span>
        ) : (
          <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-400">No league yet</span>
        )}
      </div>

      {/* Members */}
      {team.players && team.players.length > 0 && (
        <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
          {team.players.slice(0, 2).map((p, i) => (
            <span
              key={`${p.userId}-${i}`}
              className="h-6 w-6 rounded-full bg-primary-200 text-primary-700 flex items-center justify-center text-xs font-medium"
              aria-hidden="true"
            >
              {getInitials(p.userId.slice(0, 4))}
            </span>
          ))}
          <span className="text-xs text-gray-500">
            {team.players.length} member{team.players.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Footer — a hint/CTA; the status itself is shown by the badge above. */}
      <div className="mt-3 flex items-center justify-between text-sm">
        {isAwaiting ? (
          <span className="text-gray-400">Awaiting partner acceptance</span>
        ) : !hasEnrollments ? (
          <span className="text-primary-600 font-medium hover:underline">
            Enroll in a division &rarr;
          </span>
        ) : (
          <span className="text-primary-600 font-medium hover:underline">View team &rarr;</span>
        )}
      </div>
    </Link>
  );
};

// ─── Skeleton ────────────────────────────────────────────────────────────────

function TeamCardSkeleton() {
  return (
    <div className="bg-white rounded-lg shadow-sm p-5 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="h-4 bg-gray-200 rounded w-1/2" />
        <div className="h-4 bg-gray-200 rounded w-16" />
      </div>
      <div className="mt-3 flex gap-2">
        <div className="h-3 bg-gray-200 rounded w-16" />
        <div className="h-3 bg-gray-200 rounded w-20" />
      </div>
      <div className="mt-3 h-3 bg-gray-200 rounded w-1/3" />
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const TeamsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { leagueId } = useParams<{ leagueId: string }>();

  const { teams, myTeams, partnerRequests, loading, error } = useSelector(
    (state: RootState) => state.team,
  );
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [search, setSearch] = useState<string>('');

  // Scope: signed-in players default to their own teams and can expand to all
  // teams in the league. Logged-out visitors always see all teams.
  const [scope, setScope] = useState<Scope>('mine');
  const showOnlyMine = isAuthenticated && scope === 'mine';

  // Resolve tenant slug from URL leagueId — gates data fetches to avoid registry leaks.
  const { resolved: tenantResolved, leagueName: discoveredLeagueName } =
    useActiveLeagueTenant(leagueId);

  // Fetch league teams (public, but still needs correct tenant)
  useEffect(() => {
    if (leagueId && tenantResolved) {
      dispatch(fetchLeagueTeams({ leagueId }));
    }
    return () => {
      dispatch(clearTeamError());
    };
  }, [dispatch, leagueId, tenantResolved]);

  // Fetch my teams only once the active tenant slug is set — these are league-scoped
  // and must hit /api/<slug>/v1/teams/me, not the registry.
  useEffect(() => {
    if (isAuthenticated && tenantResolved) {
      dispatch(fetchMyTeams());
      // Incoming pair-team invitations awaiting this user's acceptance.
      dispatch(fetchPartnerRequests());
    }
  }, [dispatch, isAuthenticated, tenantResolved]);

  const handleAcceptPartner = useCallback(
    (teamId: string) => {
      dispatch(acceptPartnerRequest(teamId));
    },
    [dispatch],
  );

  const handleDeclinePartner = useCallback(
    (teamId: string) => {
      dispatch(declinePartnerRequest(teamId));
    },
    [dispatch],
  );

  // Client-side filtering of league teams — search by name only
  const filteredTeams = teams.filter(team => {
    if (categoryFilter) {
      // filter by whether any enrollment matches the category
      const hasCategory = team.enrollments?.some(e => e.category === categoryFilter) ?? false;
      if (!hasCategory) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      if (!team.name.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const handleCreateTeam = useCallback(() => {
    if (!isAuthenticated) {
      navigate(`/login?returnTo=/teams/new`);
    } else {
      navigate('/teams/new');
    }
  }, [isAuthenticated, navigate]);

  const hasFilters = categoryFilter !== '' || search !== '';

  // Use discovered league name from the hook (already looked up from bannerLeagues)
  const selectedLeagueName = discoveredLeagueName;

  // De-dupe by id — accepting a partner request can otherwise leave the same team
  // in myTeams twice, showing a duplicate card.
  const uniqueMyTeams = useMemo(() => {
    const byId = new Map<string, Team>();
    for (const t of myTeams) byId.set(t.id, t);
    return Array.from(byId.values());
  }, [myTeams]);

  return (
    <div>
      {/* Page header */}
      <div className="pb-4 border-b border-gray-200 flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Teams</h1>
          {selectedLeagueName && <p className="text-sm text-gray-500 mt-1">{selectedLeagueName}</p>}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleCreateTeam}
            className="px-4 py-2 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
          >
            Create team
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mt-4 rounded-md bg-danger-50 p-3" role="alert">
          <p className="text-sm text-danger-800">{error}</p>
        </div>
      )}

      {/* Incoming partner requests — pair-team invitations awaiting this user's
          acceptance. Shown to the invited partner so they can approve/decline. */}
      {isAuthenticated && partnerRequests.length > 0 && (
        <section className="mt-6" aria-labelledby="partner-requests-heading">
          <h2
            id="partner-requests-heading"
            className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3"
          >
            Partner requests
          </h2>
          <PartnerRequestsList
            requests={partnerRequests}
            onAccept={handleAcceptPartner}
            onDecline={handleDeclinePartner}
            loading={loading}
          />
        </section>
      )}

      {/* Scope toggle: my teams vs all teams in the league (logged-in only) */}
      {isAuthenticated && (
        <div className="mt-6">
          <ScopeToggle
            scope={scope}
            onChange={setScope}
            mineLabel="My teams"
            allLabel="All teams"
            ariaLabel="Show my teams or all teams"
          />
        </div>
      )}

      {/* My teams section ("mine" scope) */}
      {showOnlyMine && (
        <section className="mt-6" aria-labelledby="my-teams-heading">
          <h2
            id="my-teams-heading"
            className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3"
          >
            My Teams
          </h2>
          {myTeams.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {uniqueMyTeams.map(team => (
                <TeamCard key={team.id} team={team} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg shadow-sm">
              <p className="text-gray-500">You are not on any teams yet.</p>
              <button
                type="button"
                onClick={() => setScope('all')}
                className="mt-3 text-sm text-primary-600 hover:underline"
              >
                Show all teams
              </button>
            </div>
          )}
        </section>
      )}

      {/* All teams in the league ("all" scope, or logged-out). Filters apply here. */}
      {!showOnlyMine && (
        <>
          {/* Filters */}
          <div className="mt-6 flex flex-wrap gap-3 items-center">
            <select
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
              className="rounded-md border-gray-300 border text-sm shadow-sm px-3 py-2 bg-white focus:ring-primary-500 focus:border-primary-500"
              aria-label="Filter by category"
            >
              <option value="">All categories</option>
              {ALL_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>

            <input
              type="search"
              placeholder="Search team or player…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="rounded-md border border-gray-300 text-sm shadow-sm px-3 py-2 bg-white flex-1 min-w-[12rem] focus:ring-primary-500 focus:border-primary-500"
              aria-label="Search teams"
            />

            {hasFilters && (
              <button
                type="button"
                onClick={() => {
                  setCategoryFilter('');
                  setSearch('');
                }}
                className="text-sm text-primary-600 hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>

          {/* League teams grid */}
          <section className="mt-6" aria-labelledby="league-teams-heading">
            <h2
              id="league-teams-heading"
              className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3"
            >
              {selectedLeagueName ? `${selectedLeagueName} Teams` : 'League Teams'}
            </h2>

            {loading && teams.length === 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {[1, 2, 3].map(n => (
                  <TeamCardSkeleton key={n} />
                ))}
              </div>
            ) : filteredTeams.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                {hasFilters ? (
                  <>
                    <p className="text-gray-500">No teams match your filters.</p>
                    <button
                      type="button"
                      onClick={() => {
                        setCategoryFilter('');
                        setSearch('');
                      }}
                      className="mt-2 text-sm text-primary-600 hover:underline"
                    >
                      Clear filters
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-gray-500">
                      No teams registered in {selectedLeagueName ?? 'this league'} yet.
                    </p>
                    {!isAuthenticated && (
                      <p className="mt-1 text-sm text-gray-400">
                        <Link to="/login" className="text-primary-600 hover:underline">
                          Log in
                        </Link>{' '}
                        to create a team.
                      </p>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredTeams.map(team => (
                  <TeamCard key={team.id} team={team} />
                ))}
              </div>
            )}
          </section>
        </>
      )}

      <p className="mt-6 text-xs text-gray-400">
        Browsing is public; creating a team requires logging in.
      </p>
    </div>
  );
};

export default TeamsPage;
